import { useMemo, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { Flex } from 'foundations-components/components/Flex';
import { Illustration } from 'foundations-components/transitional/components/Illustration';
import { SelectableBox } from 'foundations-components/transitional/components/SelectableBox';
import SelectEmbedStyleV2 from 'MeetingsAdmin/components/embed/SelectEmbedStyleV2';
import useSendEmbedCodeEmail from '../../hooks/useSendEmbedCodeEmail';
import { DialogHeaderImage } from 'foundations-components/transitional/components/DialogHeaderImage';
import MeetingsPopupPreviewImage from 'web-interactives-components/integrations/meetings/MeetingPopupPreviewImage';
import { CreateMeetingPopupButton } from 'web-interactives-components/integrations/meetings/CreateMeetingPopupButton';
import { useSelector } from 'react-redux';
import { CopyInput } from 'foundations-components/transitional/components/CopyInput';
import { Small } from 'foundations-components/transitional/components/Small';
import { H5 } from 'foundations-components/transitional/components/H5';
import { H7 } from 'foundations-components/transitional/components/H7';
import { H2 } from 'foundations-components/transitional/components/H2';
import { Button } from 'foundations-components/transitional/components/Button';
// @ts-expect-error not migrated yet
import { getHasCtasEditAccess } from '../../selectors/AuthSelector';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const ILLUSTRATION_HEIGHT = 64;
const EMBED = 'embed';
const SHARE_LINK = 'shareLink';
const POPUP = 'popup';
const ShareOptionButton = ({
  option,
  illustration,
  title,
  description,
  selectedOption,
  handleSelectOption
}) => {
  return /*#__PURE__*/_jsxs(SelectableBox, {
    flexBasis: "100%",
    type: "default",
    selected: selectedOption === option,
    onClick: () => handleSelectOption(option),
    textAlign: "center",
    children: [illustration, /*#__PURE__*/_jsx(H5, {
      children: title
    }), /*#__PURE__*/_jsx(Small, {
      children: description
    })]
  });
};
function PostPublishModal({
  onClose,
  link
}) {
  const [selectedOption, setSelectedOption] = useState(null);
  const onSendEmbedCodeEmail = useSendEmbedCodeEmail();
  const hasCtasEditAccess = useSelector(getHasCtasEditAccess);
  const shouldShowCtaOption = hasCtasEditAccess && !link.paymentLinkId;
  const content = useMemo(() => {
    switch (selectedOption) {
      case POPUP:
        return /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(H7, {
            marginBlockEnd: "200",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "postPublishModal.addToPopup.cta"
            })
          }), /*#__PURE__*/_jsx(Flex, {
            flexDirection: "column",
            gap: "100",
            alignItems: "center",
            children: /*#__PURE__*/_jsx(MeetingsPopupPreviewImage, {
              maxInlineSize: "100%"
            })
          })]
        });
      case EMBED:
        return /*#__PURE__*/_jsx(SelectEmbedStyleV2, {
          link: link,
          onSendEmbedCodeEmail: onSendEmbedCodeEmail
        });
      case SHARE_LINK:
        return /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(H7, {
            marginBlockEnd: "200",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "postPublishModal.copyShareLink.copyInputLabel"
            })
          }), /*#__PURE__*/_jsx(CopyInput, {
            buttonProps: {
              use: 'tertiary'
            },
            value: link.link
          })]
        });
      default:
        return null;
    }
  }, [selectedOption, link, onSendEmbedCodeEmail]);
  const options = useMemo(() => {
    const handleSelectOption = option => {
      setSelectedOption(option);
    };
    return /*#__PURE__*/_jsxs(Flex, {
      justifyContent: "center",
      gap: "400",
      marginBlock: "500",
      children: [shouldShowCtaOption && /*#__PURE__*/_jsx(ShareOptionButton, {
        option: POPUP,
        illustration: /*#__PURE__*/_jsx(Illustration, {
          name: "website-platform-monitor",
          height: ILLUSTRATION_HEIGHT
        }),
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "postPublishModal.addToPopup.title"
        }),
        description: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "postPublishModal.addToPopup.description"
        }),
        selectedOption: selectedOption,
        handleSelectOption: handleSelectOption
      }), /*#__PURE__*/_jsx(ShareOptionButton, {
        option: EMBED,
        illustration: /*#__PURE__*/_jsx(Illustration, {
          name: "mobile-onboarding-embed-tracking-code",
          height: ILLUSTRATION_HEIGHT
        }),
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "postPublishModal.getEmbedCode.title"
        }),
        description: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "postPublishModal.getEmbedCode.description"
        }),
        selectedOption: selectedOption,
        handleSelectOption: handleSelectOption
      }), /*#__PURE__*/_jsx(ShareOptionButton, {
        option: SHARE_LINK,
        illustration: /*#__PURE__*/_jsx(Illustration, {
          name: "copy-paste",
          height: ILLUSTRATION_HEIGHT
        }),
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "postPublishModal.copyShareLink.title"
        }),
        description: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "postPublishModal.copyShareLink.description"
        }),
        selectedOption: selectedOption,
        handleSelectOption: handleSelectOption
      })]
    });
  }, [shouldShowCtaOption, selectedOption]);
  return /*#__PURE__*/_jsxs(Modal, {
    width: 800,
    use: "success",
    "data-test-id": "post-publish-modal",
    children: [/*#__PURE__*/_jsx(DialogHeaderImage, {
      offsetBottom: -30,
      offsetTop: 59,
      children: /*#__PURE__*/_jsx(Illustration, {
        name: "success-green",
        width: 118
      })
    }), /*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(H2, {
        inlineSize: "100%",
        textAlign: "center",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "postPublishModal.title"
        })
      }), /*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onClose
      })]
    }), /*#__PURE__*/_jsxs(DialogBody, {
      children: [/*#__PURE__*/_jsx(H7, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "postPublishModal.chooseHowToShare"
        })
      }), options, content]
    }), /*#__PURE__*/_jsxs(DialogFooter, {
      align: "right",
      children: [/*#__PURE__*/_jsx(Button, {
        use: "secondary",
        onClick: onClose,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "postPublishModal.close"
        })
      }), selectedOption === POPUP && /*#__PURE__*/_jsx(CreateMeetingPopupButton, {
        meetingLink: link.link
      })]
    })]
  });
}
export default PostPublishModal;